// components
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { Dialog } from 'components/shared';
import { Icon } from '@sdb/planning-ui';

// hooks
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useWorkingDaysSettings } from './useWorkingDaysSettings';

// styles
import {
  HintText,
  InfoBox,
  SectionPanel,
  WORK_AGREEMENT_COLORS,
  WorkAgreementMeta
} from './RegisterAbsenceDialog.styles';

// utils
import { startOfDay } from 'date-fns';
import { toBackendDateTime, toDateString } from 'utils/date';
import { getRegisterAbsenceDuties } from 'RestAPIs/roster/register-absence';
import { showSuccessSnackbar } from 'components/shared/Snackbar';
import {
  WEEKDAYS,
  isPnilContract,
  isZeroHoursContract
} from './useRegisterAbsenceDialog';

// types
import type { RegisterAbsenceWorkAgreement } from 'RestAPIs/roster/register-absence';
import type { RegisterAbsenceDialogEmployee } from './RegisterAbsenceDialog.types';
import type { Weekday } from './useRegisterAbsenceDialog';
import type { WorkingDaysSettings } from './useWorkingDaysSettings';

type WorkingDaysSettingsDialogProps = {
  open: boolean;
  employee: RegisterAbsenceDialogEmployee;
  onClose: () => void;
};

const label = (workAgreement: RegisterAbsenceWorkAgreement) =>
  [workAgreement.nr, workAgreement.brName].filter(Boolean).join(' · ');

/**
 * Records the employee's fixed working days per work agreement, so a next
 * sickness absence starts with the checkboxes already set. Deliberately
 * without percentage or rollout choice: those belong to a single absence.
 */
const WorkingDaysSettingsDialog = ({
  open,
  employee,
  onClose
}: WorkingDaysSettingsDialogProps) => {
  const { t } = useTranslation('common');
  const { getSettings, saveSettings } = useWorkingDaysSettings();
  const [activeIndex, setActiveIndex] = useState(0);
  const [draft, setDraft] = useState<WorkingDaysSettings>({});
  const [isSaving, setIsSaving] = useState(false);

  const today = startOfDay(new Date());
  const workAgreementsQuery = useQuery(
    ['workingDaysSettingsWorkAgreements', employee.id, toDateString(today)],
    async () =>
      (
        await getRegisterAbsenceDuties({
          employeeId: employee.id,
          // real instants: the backend parses these as UTC, so the local day
          // boundary has to be sent as the instant it actually is
          startDate: toBackendDateTime(today),
          endDate: toBackendDateTime(today, true)
        })
      ).data.workAgreements,
    { enabled: open }
  );

  const workAgreements = workAgreementsQuery.data ?? [];

  // start from what was recorded before, falling back to the contract flags
  useEffect(() => {
    if (!open || !workAgreements.length) {
      return;
    }
    const stored = getSettings();
    setDraft(
      Object.fromEntries(
        workAgreements.map((workAgreement) => [
          workAgreement.waId,
          stored[workAgreement.waId] ?? {
            days: WEEKDAYS.filter(
              (day) => workAgreement[`${day}Hours`] === true
            )
          }
        ])
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workAgreementsQuery.data]);

  const toggleDay = (waId: number, day: Weekday) => {
    const current = draft[waId]?.days ?? [];
    setDraft((previous) => ({
      ...previous,
      [waId]: {
        ...previous[waId],
        days: current.includes(day)
          ? current.filter((value) => value !== day)
          : [...current, day]
      }
    }));
  };

  const onSave = async () => {
    setIsSaving(true);
    const saved = await saveSettings(draft);
    setIsSaving(false);
    if (saved) {
      showSuccessSnackbar();
      onClose();
    }
  };

  const active =
    workAgreements[Math.min(activeIndex, workAgreements.length - 1)];
  const activeDraft = active ? draft[active.waId] : undefined;

  return (
    <Dialog
      open={open}
      title={`${t('reactRegisterAbsence.workingDaysSettingsTitle')} — ${[
        employee.name,
        employee.number ? `(${employee.number})` : null
      ]
        .filter(Boolean)
        .join(' ')}`}
      handleClose={onClose}
      actions={
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', p: 2 }}>
          <Button onClick={onClose}>{t('cancel')}</Button>
          <Button
            variant="contained"
            disabled={isSaving || !workAgreements.length}
            onClick={onSave}
          >
            {t('save')}
          </Button>
        </Box>
      }
    >
      <Box sx={{ p: 2, width: 'min(620px, calc(100vw - 80px))' }}>
        <HintText>
          {t('reactRegisterAbsence.workingDaysSettingsExplanation')}
        </HintText>

        {workAgreementsQuery.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {!workAgreementsQuery.isLoading && workAgreements.length > 1 && (
          <Tabs
            value={Math.min(activeIndex, workAgreements.length - 1)}
            onChange={(_, index) => setActiveIndex(index)}
            variant="scrollable"
            sx={{
              minHeight: 40,
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTabs-indicator': { height: 3 }
            }}
          >
            {workAgreements.map((workAgreement, index) => (
              <Tab
                key={workAgreement.waId}
                sx={{
                  minHeight: 40,
                  textTransform: 'none',
                  minWidth: 0,
                  fontSize: 15,
                  color:
                    WORK_AGREEMENT_COLORS[index % WORK_AGREEMENT_COLORS.length],
                  '&.Mui-selected': {
                    fontWeight: 700,
                    backgroundColor: 'action.hover',
                    borderRadius: '4px 4px 0 0',
                    color:
                      WORK_AGREEMENT_COLORS[
                        index % WORK_AGREEMENT_COLORS.length
                      ]
                  }
                }}
                iconPosition="start"
                icon={<Icon type="briefcase" fontSize="small" />}
                label={index + 1}
              />
            ))}
          </Tabs>
        )}

        {active && activeDraft && (
          <SectionPanel sx={{ mt: 2 }}>
            <Box
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color:
                  WORK_AGREEMENT_COLORS[
                    Math.min(activeIndex, workAgreements.length - 1) %
                      WORK_AGREEMENT_COLORS.length
                  ]
              }}
            >
              {t('reactRegisterAbsence.workAgreementOpen', {
                number: Math.min(activeIndex, workAgreements.length - 1) + 1,
                name: active.brName ?? label(active)
              })}
            </Box>
            <WorkAgreementMeta sx={{ mb: 1.5 }}>
              {[active.functionDescription, active.contractKind]
                .filter(Boolean)
                .join(' · ')}
            </WorkAgreementMeta>
            {/* nothing to record here for PNIL (no sickness duties are ever
                created) nor for a zero-hours contract (which has no fixed
                working days: those are chosen per sickness notice, together
                with the hours). The tab stays so it is visible the work
                agreement exists, with the reason spelled out. */}
            {isPnilContract(active) || isZeroHoursContract(active) ? (
              <InfoBox>
                {t(
                  isPnilContract(active)
                    ? 'reactRegisterAbsence.pnilNoRolloutInfo'
                    : 'reactRegisterAbsence.zeroHoursNoFixedDaysInfo'
                )}
              </InfoBox>
            ) : (
              <>
                {/* this screen records which days the employee works, not how
                    many hours: the hours belong to a single sickness notice
                    and are entered there */}
                <WorkAgreementMeta sx={{ mb: 0.25 }}>
                  {t('reactRegisterAbsence.workingDaysLabel')}
                </WorkAgreementMeta>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {WEEKDAYS.map((day) => {
                    const chosen = activeDraft.days.includes(day);
                    return (
                      <Chip
                        key={day}
                        size="small"
                        sx={{ minWidth: 48 }}
                        label={t(`reactRegisterAbsence.weekday.${day}`)}
                        color={chosen ? 'primary' : 'default'}
                        variant={chosen ? 'filled' : 'outlined'}
                        onClick={() => toggleDay(active.waId, day)}
                      />
                    );
                  })}
                </Box>
              </>
            )}
          </SectionPanel>
        )}
      </Box>
    </Dialog>
  );
};

export default WorkingDaysSettingsDialog;
